import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import client from "libs/client";
import type { GameHistoryNoteResponse } from "libs/apiClient";

export type GameHistoryApiType = "all" | "internal" | "log";

const PAGE_SIZE = 10;
const NEAR_BOTTOM_THRESHOLD_PX = 80;
const FIRST_PAGE = 1;

export function useDiscoverGameHistoryFeed(gameId: number, apiType: GameHistoryApiType) {
	const feedRef = useRef<HTMLDivElement>(null);
	const pendingInitialScrollRef = useRef(true);
	const isNearBottomRef = useRef(true);

	const [page, setPage] = useState<number>(FIRST_PAGE);
	const [olderItems, setOlderItems] = useState<GameHistoryNoteResponse[]>([]);
	const addedOlderPagesRef = useRef<Set<number>>(new Set());

	const latestQuery = client.getDiscoverGameHistoryNotes.useQuery(
		{
			id: gameId,
			type: apiType,
			page: FIRST_PAGE,
			limit: PAGE_SIZE,
		},
		{ queryKey: ["historyFeed", "latest", gameId, apiType] },
	);

	const olderPageQuery = client.getDiscoverGameHistoryNotes.useQuery(
		{
			id: gameId,
			type: apiType,
			page,
			limit: PAGE_SIZE,
		},
		{
			queryKey: ["historyFeed", "older", gameId, apiType, page],
			enabled: page > FIRST_PAGE,
		},
	);

	const latestItems = latestQuery.data?.data.items ?? [];
	const total = latestQuery.data?.data.total ?? 0;

	const allItems = useMemo<GameHistoryNoteResponse[]>(
		() => [...olderItems, ...latestItems],
		[olderItems, latestItems],
	);

	const isLoading = latestQuery.isLoading;
	const isLoadingMore = page > FIRST_PAGE && olderPageQuery.isFetching === true;
	const canLoadMore = total > 0 && allItems.length < total;

	const latestTailId = allItems.length > 0 ? allItems[allItems.length - 1]?.id : null;

	const scrollFeedToBottom = useCallback(() => {
		const el = feedRef.current;
		if (el === null) {
			return;
		}
		el.scrollTop = el.scrollHeight;
	}, []);

	const resetLoadedOlderPages = useCallback(() => {
		addedOlderPagesRef.current.clear();
		setOlderItems([]);
		setPage(FIRST_PAGE);
	}, []);

	const loadOlderItems = useCallback(() => {
		if (isLoadingMore === true || canLoadMore === false) {
			return;
		}
		setPage((prev) => prev + 1);
	}, [isLoadingMore, canLoadMore]);

	const handleScroll = useCallback(() => {
		const el = feedRef.current;
		if (el === null) {
			return;
		}

		const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
		isNearBottomRef.current = distanceFromBottom <= NEAR_BOTTOM_THRESHOLD_PX;

		const isAtTop = el.scrollTop <= 0;
		if (isAtTop === true && isLoadingMore === false && canLoadMore === true) {
			loadOlderItems();
		}
	}, [isLoadingMore, canLoadMore, loadOlderItems]);

	useEffect(() => {
		addedOlderPagesRef.current.clear();
		setOlderItems([]);
		setPage(FIRST_PAGE);
		pendingInitialScrollRef.current = true;
		isNearBottomRef.current = true;
	}, [gameId, apiType]);

	useEffect(() => {
		const res = olderPageQuery.data;
		if (page === FIRST_PAGE || res === undefined) {
			return;
		}
		if (addedOlderPagesRef.current.has(page) === true) {
			return;
		}
		addedOlderPagesRef.current.add(page);
		setOlderItems((prev) => [...res.data.items, ...prev]);
	}, [olderPageQuery.data, page]);

	useEffect(() => {
		if (latestTailId === null) {
			return;
		}

		if (pendingInitialScrollRef.current === true) {
			pendingInitialScrollRef.current = false;
			requestAnimationFrame(() => scrollFeedToBottom());
			return;
		}

		if (isNearBottomRef.current === true) {
			requestAnimationFrame(() => scrollFeedToBottom());
		}
	}, [latestTailId, scrollFeedToBottom]);

	return {
		feedRef,
		isLoading,
		allItems,
		isLoadingMore,
		canLoadMore,
		resetLoadedOlderPages,
		handleScroll,
		scrollFeedToBottom,
	};
}
